import { type RefObject, useCallback, useRef } from "react";
import { selectOpenedDetailDishTypeId, setOpenedDetailDishTypeId } from "slices/appStateSlice";
import { LightingModeType } from "types";
import type { LocalDishType } from "types/mainTypes";
import { filterAvailableDishTypeIds } from "utils/mealListUtils";
import { scrollIntoView } from "utils/scrollIntoView";
import { useFilters } from "./useFilters";
import { useFridgeLights } from "./useFridgeLights";
import { useAppDispatch, useAppSelector } from "./utilsHooks/storeHooks";

interface UseFridgeLightingHandlingReturn {
	handleOpen: (dishTypeId: string, ref?: RefObject<HTMLDivElement | null>) => void;
	handleLightsStateWhenFilterIsActive: () => void;
	openDishTypeDetail: (dishTypeId: string) => void;
}

/**
 * Manages the events related to the use of meal list.
 * @param {string[]} availableDishTypeIds - The list of available dish type IDs.
 * @param {LocalDishType[]} fridgeDishes - The list of local dish types.
 * @return {useFridgeLightingHandlingReturn} - An object that contains event handling functions.
 */
const useMealListEvents = (
	availableDishTypeIds: Set<string>,
	fridgeDishes: LocalDishType[],
): UseFridgeLightingHandlingReturn => {
	const { allergens } = useFilters();
	const openDetailDishTypeId = useAppSelector(selectOpenedDetailDishTypeId);
	const dispatch = useAppDispatch();

	const { toggleLight, toggleFridgeLightingMode, highlightFilteredDishes, lights } = useFridgeLights();

	// Refs for values that change frequently — used in stable callbacks
	const openDetailDishTypeIdRef = useRef(openDetailDishTypeId);
	openDetailDishTypeIdRef.current = openDetailDishTypeId;
	const allergensRef = useRef(allergens);
	allergensRef.current = allergens;
	const fridgeDishesRef = useRef(fridgeDishes);
	fridgeDishesRef.current = fridgeDishes;
	const availableDishTypeIdsRef = useRef(availableDishTypeIds);
	availableDishTypeIdsRef.current = availableDishTypeIds;
	const lightsRef = useRef(lights);
	lightsRef.current = lights;

	const closeOpenedDishTypeDetail = useCallback(() => {
		dispatch(setOpenedDetailDishTypeId(null));
	}, [dispatch]);

	const openDishTypeDetail = useCallback(
		(dishTypeId: string) => {
			dispatch(setOpenedDetailDishTypeId(dishTypeId));
		},
		[dispatch],
	);

	const handleOpen = useCallback(
		(dishTypeId: string, ref?: RefObject<HTMLDivElement | null>) => {
			if (ref) {
				scrollIntoView(ref);
			}
			if (openDetailDishTypeIdRef.current === dishTypeId) {
				closeOpenedDishTypeDetail();
				return;
			}
			openDishTypeDetail(dishTypeId);
		},
		[closeOpenedDishTypeDetail, openDishTypeDetail],
	);

	const handleLightsStateWhenFilterIsActive = useCallback(() => {
		const filteredDishTypesIds = filterAvailableDishTypeIds(
			fridgeDishesRef.current,
			availableDishTypeIdsRef.current,
			allergensRef.current,
		);
		const openDetailId = openDetailDishTypeIdRef.current;
		// Close the open detail only if it no longer passes the active filter — keeping it open
		// when it still matches preserves the per-dish spotlight that drove its highlight.
		if (openDetailId && !filteredDishTypesIds.includes(openDetailId)) {
			closeOpenedDishTypeDetail();
		}
		// If there are any dish types that match the filters, we highlight them
		if (allergensRef.current.length > 0 && filteredDishTypesIds.length > 0) {
			highlightFilteredDishes(filteredDishTypesIds, availableDishTypeIdsRef.current, toggleLight, openDetailId);
		} else if (lightsRef.current !== LightingModeType.StandBy) {
			// If no dish types match the filters, set the fridge lights to StandBy.
			// Update `lightsRef.current` synchronously before the mutation — mirrors the invariant
			// every callsite in MealList.tsx (effect 1/2/3) relies on so a synchronous repeated
			// call inside the same commit can't see the pre-mutation value and re-dispatch.
			lightsRef.current = LightingModeType.StandBy;
			toggleFridgeLightingMode(LightingModeType.StandBy);
		}
	}, [closeOpenedDishTypeDetail, highlightFilteredDishes, toggleLight, toggleFridgeLightingMode]);

	return {
		handleOpen: handleOpen,
		handleLightsStateWhenFilterIsActive: handleLightsStateWhenFilterIsActive,
		openDishTypeDetail: openDishTypeDetail,
	};
};

export default useMealListEvents;
